import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import InfoTip from '../../reusable/InfoTip/InfoTip';
import { t } from '../../i18n/i18n';
import './AutoRefresh.scss';

/** The bounds the trader may set Account Metrics' auto-refresh to, in seconds. */
export const REFRESH_MIN = 10;
export const REFRESH_MAX = 30;
export const REFRESH_DEFAULT = 30;
const PRESETS = [10, 15, 20, 30];
const STORAGE_KEY = 'am-refresh-seconds';

const clamp = (n) => Math.min(REFRESH_MAX, Math.max(REFRESH_MIN, Math.round(n)));

/**
 * The interval last chosen on this browser — a per-viewer convenience, so it
 * survives a reload. Storage can be blocked (private windows, cleared site
 * data), in which case the default stands.
 */
export const readRefreshSeconds = () => {
  try {
    const n = Number(window.localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(n) && n > 0 ? clamp(n) : REFRESH_DEFAULT;
  } catch {
    return REFRESH_DEFAULT;
  }
};

const saveRefreshSeconds = (seconds) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(seconds));
  } catch {
    // Not remembered this time; the choice still applies to this visit.
  }
};

/**
 * How often Account Metrics refreshes itself: a trigger in the top bar
 * ("30s"), opening a small menu of presets between 10 and 30 seconds and a
 * custom field held to the same bounds.
 *
 * The menu is portalled and placed `fixed` from the trigger's rect: on a phone
 * the top bar's slot scrolls sideways, and a menu inside it would be clipped.
 */
export default function AutoRefresh({ seconds, onChange, lastRefreshedAt }) {
  const [open, setOpen] = useState(false);

  /*
   * The countdown to the next refresh, ticking each second: from when the
   * page's data last arrived plus the interval. Its own state, so only this
   * control re-renders each second. At zero it says it's refreshing — the next
   * read moves `lastRefreshedAt` on and the count starts again. Paused tabs
   * don't poll, so on return it may sit at "Refreshing…" for a moment.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const left = lastRefreshedAt
    ? Math.max(0, Math.ceil((lastRefreshedAt + seconds * 1000 - now) / 1000))
    : null;
  // A one-digit count is padded with a figure space (a digit's width), so
  // "9s" and "10s" take the same room.
  const countdown =
    left == null
      ? t('Every {{seconds}}s', { seconds })
      : left === 0
        ? t('Refreshing…')
        : t('Refresh in {{seconds}}s', { seconds: String(left).padStart(2, '\u2007') });
  // The phone form: just the count, the icon saying what it counts to.
  const shortCountdown =
    left == null ? `${seconds}s` : left === 0 ? '…' : `${String(left).padStart(2, '\u2007')}s`;
  const [custom, setCustom] = useState('');
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const customValue = Number(custom);
  const customValid =
    custom !== '' && Number.isInteger(customValue) && customValue >= REFRESH_MIN && customValue <= REFRESH_MAX;

  const choose = (next) => {
    const value = clamp(next);
    saveRefreshSeconds(value);
    onChange(value);
    setOpen(false);
    setCustom('');
  };

  // Placed under the trigger, right edges aligned, once it opens — and kept
  // inside the screen: on a phone the trigger sits at the row's left, where a
  // right-aligned 232px menu would run off the edge.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = Math.min(232, window.innerWidth - 24);
    const left = Math.min(Math.max(12, rect.right - width), window.innerWidth - width - 12);
    setPos({ top: rect.bottom + 8, left });
  }, [open]);

  // Closes on a click outside, Escape, or the page moving under it.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDown = (event) => {
      if (menuRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return;
      close();
    };
    const onKey = (event) => event.key === 'Escape' && close();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  return (
    <>
      {/* One pill holding two controls: the (i) and the interval button. A
          wrapper rather than one <button>, since a button can't hold another
          interactive element — and the (i) explaining the control mustn't open
          its menu. The menu still lines up with the whole pill. */}
      <div ref={triggerRef} className={`am-refresh${open ? ' is-open' : ''}`}>
        {/* What the control is about, in plain words. */}
        <InfoTip
          className="am-refresh__help"
          label={t('How auto-refresh works')}
          title={t('Auto-refresh')}
          glyph="i"
        >
          <span>{t('Your data updates every {{seconds}}s — no need to reload.', { seconds })}</span>
          <span className="am-refresh__tip">
            <strong>{t('Tip:')}</strong> {t('click the timer to pick {{min}}–{{max}}s or set your own.', {
              min: REFRESH_MIN,
              max: REFRESH_MAX,
            })}
          </span>
        </InfoTip>

        <button
          type="button"
          className="am-refresh__trigger"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={t('Auto-refresh every {{seconds}} seconds — change', { seconds })}
          onClick={() => setOpen((v) => !v)}
        >
          <span
            className={`icon am-refresh__icon${left === 0 ? ' is-spinning' : ''}`}
            style={{ '--icon': 'url(/icons/refresh.svg)' }}
          />
          {/* Sized to its longest wording by a hidden copy in the same grid
              cell, so the button holds one width whatever the count says —
              1 vs 10, or "Refreshing…" — instead of jittering every second. */}
          <span className="am-refresh__count" aria-live="off">
            <span className="am-refresh__count-sizer" aria-hidden="true">
              {t('Refresh in {{seconds}}s', { seconds: REFRESH_MAX })}
            </span>
            <span className="am-refresh__count-text">{countdown}</span>
          </span>
          {/* The same count, shorter, for phones — swapped in by CSS. */}
          <span className="am-refresh__count am-refresh__count--short" aria-hidden="true">
            <span className="am-refresh__count-sizer">{REFRESH_MAX}s</span>
            <span className="am-refresh__count-text">{shortCountdown}</span>
          </span>
        </button>
      </div>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="am-refresh__menu"
            role="dialog"
            aria-label={t('Auto-refresh interval')}
            style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
          >
            <span className="am-refresh__title">{t('Auto-refresh every {{seconds}} seconds', { seconds })}</span>

            <div className="am-refresh__presets">
              {PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`am-refresh__preset${preset === seconds ? ' is-active' : ''}`}
                  aria-pressed={preset === seconds}
                  onClick={() => choose(preset)}
                >
                  {preset}s
                </button>
              ))}
            </div>

            <form
              className="am-refresh__custom"
              onSubmit={(event) => {
                event.preventDefault();
                if (customValid) choose(customValue);
              }}
            >
              <label className="am-refresh__field">
                <span className="am-refresh__field-label">{t('Custom')}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={REFRESH_MIN}
                  max={REFRESH_MAX}
                  step={1}
                  placeholder={`${REFRESH_MIN}–${REFRESH_MAX}`}
                  value={custom}
                  onChange={(event) => setCustom(event.target.value.replace(/\D/g, '').slice(0, 2))}
                  aria-invalid={custom !== '' && !customValid}
                />
                <span className="am-refresh__unit">{t('sec')}</span>
              </label>
              <button type="submit" className="am-refresh__set" disabled={!customValid}>
                {t('Set')}
              </button>
            </form>

            <span className={`am-refresh__hint${custom !== '' && !customValid ? ' is-error' : ''}`}>
              {t('Between {{min}} and {{max}} seconds.', { min: REFRESH_MIN, max: REFRESH_MAX })}
            </span>
          </div>,
          document.body
        )}
    </>
  );
}
